import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Copy, Plus, Printer, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { ShiftDialog } from "@/pages/admin/ShiftDialog";
import { RotaGrid } from "@/pages/admin/RotaGrid";
import { RotaPrintTable } from "@/pages/admin/RotaPrintTable";
import { RotaViewBar } from "@/pages/admin/RotaViewBar";
import { api, errMsg, money } from "@/lib/api";
import { ymd } from "@/lib/dates";
import { clearView, defaultView, fromBusinessDefault, loadView, periodDays, periodFor, sameView, saveView, toBusinessDefault } from "@/lib/rotaPeriod";

const OPEN_ROW = { id: null, name: "Open shifts", position: "Unassigned" };

function usePeriod(view, anchor) {
  const [pay, setPay] = useState(null);
  useEffect(() => {
    if (view.mode !== "pay") {
      return;
    }
    api.get("/biz/pay-period", { params: { on: ymd(anchor) } })
      .then((r) => setPay({ start: parseISO(r.data.start), end: parseISO(r.data.end), label: r.data.frequency_label }))
      .catch((e) => toast.error(errMsg(e)));
  }, [view.mode, anchor]);
  return useMemo(() => (view.mode === "pay" ? pay : periodFor(anchor, view)), [view, anchor, pay]);
}

export default function Rota() {
  const { user } = useAuth();
  const [personal, setPersonal] = useState(() => !!loadView());
  const [view, setViewState] = useState(() => loadView() || defaultView());
  const [bizDefault, setBizDefault] = useState(null);
  const [anchor, setAnchor] = useState(() => new Date());
  const [data, setData] = useState({ shifts: [], total_cost: 0 });
  const [staff, setStaff] = useState([]);
  const [types, setTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [timeoff, setTimeoff] = useState([]);
  const [dialog, setDialog] = useState({ open: false });
  const [pub, setPub] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const rawPeriod = usePeriod(view, anchor);
  const period = personal || bizDefault ? rawPeriod : null;
  const days = useMemo(() => (period ? periodDays(period) : []), [period]);
  const startStr = period ? ymd(period.start) : null;
  const endStr = period ? ymd(period.end) : null;

  const setView = (v) => {
    setViewState(v);
    saveView(v);
    setPersonal(true);
  };

  useEffect(() => {
    api.get("/biz/rota-view").then((r) => {
      setBizDefault(r.data);
      if (!loadView()) {
        setViewState(fromBusinessDefault(r.data));
      }
    }).catch(() => setBizDefault(toBusinessDefault(defaultView())));
  }, []);

  const saveDefault = async () => {
    try {
      const { data: d } = await api.put("/biz/rota-view", toBusinessDefault(view));
      setBizDefault(d);
      toast.success("Saved. Managers without their own saved view will now see this rota view");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const useDefault = () => {
    clearView();
    setPersonal(false);
    setViewState(fromBusinessDefault(bizDefault || {}));
    setAnchor(new Date());
  };

  useEffect(() => {
    if (!startStr) {
      return;
    }
    api.get("/biz/rota/publication", { params: { week_start: startStr, end: endStr } }).then((r) => setPub(r.data.published_at ? r.data : null)).catch(() => {});
  }, [startStr, endStr]);

  const loadShifts = useCallback(() => {
    if (!startStr) {
      return;
    }
    api.get("/biz/shifts", { params: { start: startStr, end: endStr } }).then((r) => setData(r.data));
  }, [startStr, endStr]);

  useEffect(() => {
    loadShifts();
  }, [loadShifts]);
  useEffect(() => {
    api.get("/biz/staff").then((r) => setStaff(r.data.filter((s) => s.active)));
    api.get("/biz/shift-types").then((r) => setTypes(r.data));
    api.get("/biz/locations").then((r) => setLocations(r.data));
    api.get("/biz/requests", { params: { status: "approved" } }).then((r) => setTimeoff(r.data.filter((x) => x.type === "timeoff")));
  }, []);

  const step = (dir) => {
    if (view.mode === "custom") {
      const len = days.length * dir;
      return setView({ ...view, customStart: ymd(addDays(period.start, len)), customEnd: ymd(addDays(period.end, len)) });
    }
    setAnchor(dir > 0 ? addDays(period.end, 1) : addDays(period.start, -1));
  };
  const goToday = () => {
    if (view.mode === "custom") {
      const len = days.length - 1;
      return setView({ ...view, customStart: ymd(new Date()), customEnd: ymd(addDays(new Date(), len)) });
    }
    setAnchor(new Date());
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const { data: r } = await api.post("/biz/rota/publish", { week_start: startStr, end: endStr });
      setPub(r);
      const who = r.republish ? `${r.staff_notified} with changes, ${r.staff_unchanged} unchanged` : `${r.staff_notified} staff`;
      toast.success(r.demo ? `Published (demo, no emails sent): ${who}` : r.republish ? `Re-published. Emailing ${r.staff_notified} staff with changes; ${r.staff_unchanged} unchanged` : `Published. Emailing ${who}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setPublishing(false);
    }
  };

  const weeksBack = Math.ceil(days.length / 7);
  const copyLast = async () => {
    try {
      const { data: r } = await api.post("/biz/shifts/copy-week", { from_start: ymd(addDays(period.start, -7 * weeksBack)), to_start: startStr, days: days.length });
      toast.success(`Copied ${r.copied} shifts${r.skipped ? ` (${r.skipped} already on the rota)` : ""}`);
      loadShifts();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const typeMap = Object.fromEntries(types.map((t) => [t.id, t]));
  const isOff = (sid, d) => timeoff.some((t) => t.staff_id === sid && t.start_date <= ymd(d) && t.end_date >= ymd(d));
  const unseen = data.shifts.filter((s) => s.staff_id && s.needs_ack && !s.acked_at).length;
  const label = period ? `${format(period.start, "EEE d MMM")} – ${format(period.end, "EEE d MMM yyyy")} (${days.length} days)` : "Loading…";
  const pubText = !pub ? "Not published yet" : pub.partial ? "Partly published, some days not sent to staff yet" : `Published ${format(new Date(pub.published_at), "d MMM HH:mm")}`;

  return (
    <div>
      <div className="print-only mb-3" data-testid="rota-print-header">
        <p className="font-display text-xl font-bold">{user?.business?.name} · Staff rota</p>
        <p className="text-sm">{label} · {pubText}</p>
      </div>
      <div className="no-print">
      <PageHeader title="Rota" subtitle={<span data-testid="rota-period-label">{label} · {pubText}</span>}>
        <div className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm" data-testid="rota-total-cost">
          Est. labour <span className="font-display font-bold text-indigo-600">{money(data.total_cost)}</span>
        </div>
        <Button variant="outline" size="icon" disabled={!period} onClick={() => step(-1)} data-testid="rota-prev-week-btn"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={goToday} data-testid="rota-today-btn">Today</Button>
        <Button variant="outline" size="icon" disabled={!period} onClick={() => step(1)} data-testid="rota-next-week-btn"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="outline" disabled={!period} onClick={copyLast} data-testid="rota-copy-week-btn" title="Copies shifts onto the same weekdays">
          <Copy className="mr-1 h-4 w-4" />{weeksBack === 1 ? "Copy last week" : `Copy previous ${weeksBack} weeks`}
        </Button>
        <Button variant="outline" className="rounded-full border-emerald-600 text-emerald-700 hover:bg-emerald-50" disabled={publishing || !period} onClick={publish} data-testid="rota-publish-btn">
          <Send className="mr-1 h-4 w-4" />{publishing ? "Publishing…" : pub ? "Re-publish changes" : "Publish & notify staff"}
        </Button>
        <Button variant="outline" disabled={!period} onClick={() => window.print()} data-testid="rota-print-btn"><Printer className="mr-1 h-4 w-4" />Print</Button>
        <Button className="rounded-full" disabled={!period} onClick={() => setDialog({ open: true, preset: { date: startStr } })} data-testid="rota-add-shift-btn"><Plus className="mr-1 h-4 w-4" />Add shift</Button>
      </PageHeader>
      </div>

      <RotaViewBar view={view} isDefault={!!bizDefault && sameView(view, bizDefault)} personal={personal}
        onSaveDefault={saveDefault} onUseDefault={useDefault} onChange={(v) => {
        setView(v);
        if (v.mode !== view.mode && v.mode !== "custom") {
          setAnchor(new Date());
        }
      }} />

      {period && (
        <RotaGrid layout={view.layout} days={days} rows={[...staff, OPEN_ROW]} shifts={data.shifts} types={typeMap} isOff={isOff}
          onAdd={(r, day) => setDialog({ open: true, preset: { staff_id: r.id, date: ymd(day) } })}
          onEdit={(s) => setDialog({ open: true, shift: s })} />
      )}
      {period && <RotaPrintTable layout={view.layout} days={days} rows={[...staff, OPEN_ROW]} shifts={data.shifts} types={typeMap} isOff={isOff} />}
      <p className="print-only mt-2 text-[9px] text-slate-500">Printed {format(new Date(), "d MMM yyyy HH:mm")} from StaffRotas.com. Check the app for the latest changes.</p>
      <p className="mt-3 text-xs text-slate-500"><span className="rounded bg-slate-900 px-1 text-white">+1</span> = shift finishes the next day. Sleepover pay excludes unpaid sleep hours and adds the allowance. <span className="rounded-full bg-amber-100 px-1.5 text-amber-800">Not seen</span> / <span className="rounded-full bg-emerald-50 px-1.5 text-emerald-700">Seen</span> = whether staff have tapped "Got it" on a changed or extra shift.</p>
      {unseen > 0 && <p className="mt-2 text-sm font-semibold text-amber-700" data-testid="rota-unseen-count">{unseen} changed or extra shift{unseen === 1 ? "" : "s"} not yet confirmed by staff in this period</p>}

      <ShiftDialog open={dialog.open} onOpenChange={(o) => setDialog(o ? dialog : { open: false })} shift={dialog.shift} preset={dialog.preset}
        staff={staff} types={types} locations={locations} onSaved={loadShifts} />
    </div>
  );
}
